/**
 * # Time Text Input
 * A time text input container.
 *
 * @example
 * new TimeInput(el, {
 *   // Optional. Callback for when the input value changes.
 *   onChange(value, inputInstance) {}
 * });
 *
 * @module components/time-input.js
 */

import BaseComponent from './base';
import TimeSet from './time-set';
import padNumber from '../helpers/util/pad';
import toggleClass from '../helpers/dom/toggle-class';
import hasClass from '../helpers/dom/has-class';
import appendChildren from '../helpers/manipulation/append-children';
import triggerEvent from '../helpers/dom/trigger-event';
import each from '../helpers/util/each';
import {string as parseStringAttribute} from '../helpers/dom/parse-attribute';
import makeElement from '../helpers/dom/make-element';
import mixin from '../helpers/util/mixin';
import {convertTimeTo12Hr} from '../helpers/time/time.js';
import messaging from '../mixins/messaging';
import validation from '../mixins/validation';

const noop = function() {};

class TimeInput extends BaseComponent {

  /**
   * TextInput constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }

    this._bindEventListenerCallbacks();
    this._initializeInputs();
    this._updateClass();
    this._addEventListeners();
  }


  /**
   * Store a reference to the needed elements.
   * @param {Element} el
   */
  _cacheElements(el) {
    this.el = el;
    this.inputEl = this.el.querySelector('[type="time"]');
    this.messageEl = this.el.querySelector('.spark-input__message') || makeElement('<span class="spark-input__message"></span>');

    if (!this.inputEl) {
      throw new Error('No <input> element present in input container!', this.el);
    }
  }


  /**
   * Parse parameters from the elements.
   */
  _parseParams() {
    this.isActive = this.isActive !== null ? this.isActive : (this.inputEl.value ? true : false);
    this.timeFormat = this.timeFormat !== null ? this.timeFormat : parseStringAttribute(this.inputEl, 'data-time-format', '12');

    this.hourAriaLabel = this.hourAriaLabel !== null ? this.hourAriaLabel : 'Please enter hour';
    this.minuteAriaLabel = this.minuteAriaLabel !== null ? this.minuteAriaLabel : 'Please enter minutes';
    this.periodAriaLabel = this.periodAriaLabel !== null && this.timeFormat === '12' ? this.periodAriaLabel : 'Please enter a.m. or p.m.';

    this.currValue = this.inputEl.value !== null ? this.inputEl.value : null;
  }


  /**
   * Setup the group of typeahead fields.
   */
  _initializeInputs() {
    this._initializeInputPieces();
    this._runTypeaheads();
  }


  /**
   * Replace the time input with a group of typeaheads.
   * Keep the time input around and store the typeahead data in there in an ISO date format.
   */
  _initializeInputPieces() {

    // Hide the original element. This will be updated as the typeahead values change
    this.inputEl.style.display = 'none';

    let els = [];

    // Create the parts needed for the individual time fields
    this.timeParts = [
      {
        "name": "hour",
        "value": "12",
        "length": 2,
        "format": "\\d\\d"
      },
      {
        "name": "divider",
        "value": ":",
        "length": 1
      },
      {
        "name": "minute",
        "value": "00",
        "length": 2,
        "format": "\\d\\d"
      },
    ];

    // If in 12hr format, add the additional fields needed for a.m./p.m.
    if (this.timeFormat === '12') {
      this.timeParts.push(
        {
          "name": "divider",
          "value": " ",
          "length": 1
        },
        {
          "name": "period",
          "value": "a.m.",
          "length": 4,
          "format": "\\D\\D\\D\\D"
        },
      );
    }

    // Create a new typeahead for each part of the parsed format, and add placeholder elements
    this.timeParts.forEach( function(part) {
      this._initializeTypeaheadPiece(els, part);
    }, this);

    // Create a holder for all the pieces
    this.piecesEl = document.createElement('span');
    this.piecesEl.className = 'spark-input__fields';

    // Add all the necessary elements
    appendChildren(this.piecesEl, els);

    // Add handlers to maintain focus if a specific input field is clicked. If a delimiter is clicked select the next closest input
    els.forEach( (part) => {
      if (hasClass(part, 'spark-input')) {
        part.addEventListener('click', (e) => {
          part.querySelector('input').focus();
          e.preventDefault();
        });
      }
    });

    // Add the pieces holder
    this.el.insertBefore(this.piecesEl, this.inputEl);

    // Set the value
    if (this.inputEl.value) {
      const val = this.timeFormat === '12' ? convertTimeTo12Hr(this.inputEl.value) : this.inputEl.value;

      let [hours, minutes] = val.split(':');
      let period;

      if (minutes.includes('a.m.') || minutes.includes('p.m.')) {
        period = minutes.substr(minutes.length - 4);
        minutes = minutes.substr(0, minutes.length - 4);
      }

      let values = {
        hour: hours,
        minute: minutes
      };

      if (this.timeFormat === '12') values.period = period;

      this.setValue(values);
      this.isActive = true;
    }
  }


  /**
   * Create a typeahead or placeholder piece.
   * @param {Array} els
   * @param {Object} part
   */
  _initializeTypeaheadPiece(els, part) {
    this.typeaheads = this.typeaheads || {};
    this.typeaheadEls = this.typeaheadEls || [];

    let el;

    switch (part.name) {
      case 'hour':
      case 'minute':
      case 'period':
        this.typeaheads[part.name] = new TimeSet({
          type: part.name,
          placeholder: part.value,
          len: part.length,
          format: part.format,
          timeFormat: this.timeFormat,
          hourAriaLabel: this.hourAriaLabel,
          minuteAriaLabel: this.minuteAriaLabel,
          periodAriaLabel: this.periodAriaLabel,
          onFocus: this._onTypeaheadFocusBound,
          onBlur: this._onTypeaheadBlurBound,
          onInput: this._onTypeaheadInputBound,
          onBackspace: this._onTypeaheadBackspaceBound,
          onEnd: this._onTypeaheadEndBound
        });
        el = this.typeaheads[part.name].typeahead.el;
        this.typeaheadEls.push(el);
        break;
      default:
        el = document.createElement('span');
        el.innerHTML = part.value;
        el.className = 'spark-input__divider';
        break;
    }

    els.push(el);
  }


  /**
   * Update the active and focus classes.
   */
  _updateClass() {
    toggleClass(this.el, 'active', this.isActive);
    toggleClass(this.el, 'focus', this.inFocus ? true : false);
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because them.
   */
  _bindEventListenerCallbacks() {
    this._onTypeaheadFocusBound = this._onTypeaheadFocus.bind(this);
    this._onTypeaheadInputBound = this._onTypeaheadInput.bind(this);
    this._onTypeaheadBackspaceBound = this._onTypeaheadBackspace.bind(this);
    this._onTypeaheadEndBound = this._onTypeaheadEnd.bind(this);
    this._onTypeaheadBlurBound = this._onTypeaheadBlur.bind(this);
    this._onVisibleChildrenBound = this._onVisibleChildren.bind(this);
    this._onInputChangeBound = this._onInputChange.bind(this);
  }


  /**
   * Add event listeners.
   */
  _addEventListeners() {
    this.inputEl.addEventListener('change', this._onInputChangeBound);
    document.addEventListener('spark.visible-children', this._onVisibleChildrenBound, true);
  }


  /**
   * Remove event listeners.
   */
  _removeEventListeners() {
    this.inputEl.removeEventListener('change', this._onInputChangeBound);
    document.removeEventListener('spark.visible-children', this._onVisibleChildrenBound, true);
  }


  /**
   * Handle the spark.visible-children event.
   * @param {Object} e
   */
  _onVisibleChildren(e) {
    if (e.target.contains(this.el)) {
      setTimeout(() => {
        this.update();
      }, 0);
    }
  }


  /**
   * Show the input by adding the active state and setting character counts (if necessary).
   */
  show() {
    if (!this.isActive) {
      this._runTypeaheads();
      this.isActive = true;
      this._updateClass();
    }

    return this;
  }


  /**
   * Hide the input by removing the active state.
   */
  hide() {
    if (!this.isActive) return this;

    this.isActive = false;
    this._updateClass();
    return this;
  }


  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.inputEl.value;
  }


  /**
   * Given an object with hour, minute and period, set the value of the input.
   * @param {Object} values
   */
  setValue(values) {
    values = values || {
      hour: '',
      minute: '',
      period: ''
    };
    let i;
    let hadValue;

    for (i in this.typeaheads) {
      if (values[i] !== undefined) {
        this.typeaheads[i].setValue(values[i]);
        hadValue = hadValue || (values[i] ? true : false);
      }
    }

    if (!this.isActive && hadValue) {
      this.isActive = true;
    }

    this._padTypeaheads();
    this._updateClass();
    this.updateInput();

    return this;
  }


  /**
   * Clear the values.
   */
  clearValue() {
    each(this.typeaheads, (k, v) => {
      v.clearValue();
    });

    this.inputEl.value = '';

    return this;
  }


  /**
   * Disable the inputs.
   */
  disable() {
    each(this.typeaheads, (k, v) => {
      v.disable();
    });

    this.inputEl.setAttribute('disabled', '');

    toggleClass(this.el, 'disabled', true);

    return this;
  }


  /**
   * Enable the inputs.
   */
  enable() {
    each(this.typeaheads, (k, v) => {
      v.enable();
    });

    this.inputEl.removeAttribute('disabled');

    toggleClass(this.el, 'disabled', false);

    return this;
  }


  /**
   * Augment default remove call w/ helper cleanup.
   * @param {Boolean} leaveElement Leave the element intact.
   */
  remove(leaveElement) {
    this._removeTypeaheads(leaveElement);
    this._removeInputPieces();

    super.remove(leaveElement);

    return this;
  }


  /**
   * Update the input values to match the typeaheads.
   */
  updateInput() {
    let inputs;

    if (this.typeaheads) {
      inputs = this.typeaheads;
    }

    if (inputs) {
      let hour = inputs.hour.getValue(true);
      let minute = inputs.minute.getValue(true).toString();
      let period;

      if (this.timeFormat === '12') {
        period = inputs.period.getValue().toLowerCase();

        if (hour === 12 && period === 'a.m.') {
          hour = 0;
        } else if ((hour >= 0 && hour < 12) && period === 'p.m.') {
          hour += 12;
        }
      }

      hour = hour.toString();

      let val = this.inputEl.value;

      // Set the value for the input if we have the necessary pieces
      if (this.timeFormat === '12' && hour !== '' && minute !== '' && period !== '') {
        this.inputEl.value = padNumber(hour, 2) + ':' + padNumber(minute, 2);
      } else if (this.timeFormat === '24' && hour !== '' && minute !== '') {
        this.inputEl.value = padNumber(hour, 2) + ':' + padNumber(minute, 2);
      } else {
        this.inputEl.value = '';
      }

      if (val !== this.inputEl.value) {
        this._pauseInputChange = true;
        triggerEvent(this.inputEl, 'change');
        (this.onChange || noop)(this.inputEl.value, this);
        this._pauseInputChange = false;
      }
    }

    this.currValue = this.inputEl.value;

    return this;
  }


  /**
   * Get current typing value.
   */
  getTypingValue() {
    let inputs;
    let result = '';

    if (this.typeaheads) {
      inputs = this.typeaheads;
    }

    if (inputs) {
      let hour = inputs.hour.getValue(true);
      let minute = inputs.minute.getValue(true);
      let period = '';

      if (this.timeFormat === '12') {
        period = inputs.period.getValue();
      }

      result = hour.toString() + minute.toString() + period.toString();
    }

    return result;
  }


  /**
   * Move the focus to a typeahead element.
   * @param {Number} i
   * @param {String} character Optional A character to add.
   */
  focus(i, character) {
    if (!this.isActive || !this.inFocus) {
      return this;
    }

    let index = this.typeaheadEls.indexOf(this.inFocus.typeahead.el);
    let sib = this.typeaheadEls[index + i];
    let typeahead;

    // If we were passed a character to prepend, find the typeahead for this element
    if (character) {
      typeahead = this._getTypeaheadByElement(sib);

      if (typeahead) {

        // If this is the period field we need to ensure that only allowed characters can be entered
        if (typeahead.type === 'period') {
          if (character.toLowerCase() === 'a' || character.toLowerCase() === 'p') {
            typeahead.typeahead.addCharacterAtIndex(character, 0);
          }
        } else {
          typeahead.typeahead.addCharacterAtIndex(character, 0);
        }
      }
    }

    if (!sib) {
      return false;
    }

    let sibInput = sib.querySelector('input');

    if (sibInput) {
      sibInput.focus();

      // If we have a typeahead (because we needed to prepend a character), move the caret
      if (typeahead) {
        typeahead.typeahead.moveCaret(1);
      }
    }

    return this;
  }


  /**
   * Move the focus to the next element.
   * @param {String} character Optional A character to add.
   */
  focusNext(character) {
    if (this.focus(1, character)) {
      if (this.inFocus && !character) {
        this.inFocus.typeahead.moveCaretToStart();
      }
    }

    return this;
  }


  /**
   * Move the focus to the next element.
   * @param {String} character Optional A character to add.
   */
  focusPrevious(character) {
    if (this.focus(-1, character)) {
      if (this.inFocus)
        this.inFocus.typeahead.moveCaretToEnd();
    }

    return this;
  }


  /**
   * Pad the typeahead input values.
   */
  _padTypeaheads() {
    if (this._pauseInputChange) return;

    this._pauseInputChange = true;

    for (let i in this.typeaheads) {
      this._padTypeahead(this.typeaheads[i]);
    }

    this._pauseInputChange = false;
  }


  /**
   * Pad the typeahead input values.
   * @param {Typeahead} typeahead
   */
  _padTypeahead(typeahead) {
    let value = typeahead.getValue().toLowerCase();

    if (value) {
      if (typeahead.type === 'period') {
        if (('a.m.'.startsWith(value)) || ('p.m.'.startsWith(value))) {
          value = value.concat('a.m.'.substring(value.length));
          typeahead.setValue(value);
        }
      } else {
        let padded = padNumber(value, typeahead.typeahead.format.length);

        if (value !== padded) typeahead.setValue(padNumber(value, typeahead.typeahead.format.length));
      }
    }
  }


  /**
   * Do any of the typeaheads have a value?
   * @return {Boolean}
   */
  _hasTypeaheadValue() {
    for (let i in this.typeaheads) {
      if (this.typeaheads[i].getValue()) {
        this.isActive = true;
        return true;
      }
    }

    return false;
  }


  /**
   * Resize the elements, to account for any changed display property.
   * @param {Element} el Optional.
   */
  update(el) {
    if (el) {
      this._removeTypeaheads();
      this._removeInputPieces();
      this._removeEventListeners();
      this._cacheElements(el);
      this._initializeInputs();
      this._updateClass();
      this._addEventListeners();
    } else {
      this._runTypeaheads();
    }

    return this;
  }


  /**
   * Remove the input pieces.
   */
  _removeInputPieces() {
    this.inputEl.style.display = '';

    // Remove elements we inserted into the DOM
    if (this.piecesEl) {
      this.piecesEl.parentNode.removeChild(this.piecesEl);
    }
  }


  /**
   * Remove typeaheads.
   * @param {Boolean} leaveElement Leave the element intact.
   */
  _removeTypeaheads(leaveElement) {
    if (this.typeaheads) {
      for (let i in this.typeaheads) {
        this.typeaheads[i].remove(leaveElement);
      }
    }
  }


  /**
   * When the typeahead gains focus.
   * @param {Number} val
   * @param {Object} typeahead
   */
  _onTypeaheadFocus(val, typeahead) {
    if (this.runningTypeaheads) return;

    if (!this._hasFocus) {
      this._hasFocus = true;
      (this.onFocus || noop)(this.inputEl.value, this);
    }

    triggerEvent(this.inputEl, 'focus');
    this.inFocus = typeahead;
    this.show();
    this._updateClass();

    if (this._blurTimer) {
      clearTimeout(this._blurTimer);
      this._blurTimer = null;
    }
  }


  /**
   * Run all typeaheads so they have placeholder values.
   */
  _runTypeaheads() {

    // Make sure we don't get into an infinite loop. Even though the logic
    // in the typeaheads should be stopping this from happening, there is
    // something in Safari where the focus and blur events fire in different
    // order than other browsers so those failsafes do not work.
    if (this.runningTypeaheads) {
      return;
    }

    this.runningTypeaheads = true;

    if (this.inFocus) {
      this.inFocus.pause();
    }

    for (let i in this.typeaheads) {
      if (this.typeaheads[i] !== this.inFocus) {
        this.typeaheads[i].run();
      }
    }

    if (this.inFocus) {
      this.inFocus.resume();
      this.inFocus.run();
    }

    this.runningTypeaheads = false;
  }


  /**
   * Get the typeahead that corresponds to the given element.
   * @param {Element} el
   * @return {Object}
   */
  _getTypeaheadByElement(el) {
    for (let i in this.typeaheads) {
      if (this.typeaheads[i].typeahead.el === el) {
        return this.typeaheads[i];
      }
    }
  }


  /**
   * When the typeahead loses focus, make sure numbers are padded properly.
   * @param {Number} val
   * @param {Object} typeahead
   */
  _onTypeaheadBlur(val, typeahead) {
    if (this.runningTypeaheads) return;

    this.inFocus = null;

    this._padTypeahead(typeahead);
    this.updateInput();

    if (!this.inputEl.value && !this._hasTypeaheadValue()) {
      this.hide();
    }

    this._updateClass();

    this._blurTimer = setTimeout(function() {
      this._hasFocus = false;
      (this.onBlur || noop)(this.inputEl.value, this);
    }.bind(this), 1);
  }


  /**
   * `input` event callback for typeahead.
   */
  _onTypeaheadInput() {
    (this.onInput || noop)(this.getTypingValue(), this);
  }


  /**
   * When the typeahead fires a backspace event, move back to the previous input.
   * @param {Number} val
   * @param {Object} typeahead
   */
  _onTypeaheadBackspace() {
    this.focusPrevious();
  }


  /**
   * When the typeahead is at its maximum length and the caret is at the end,
   * focus on the next input field.
   * @param {Object} typeahead
   * @param {String} character Optional
   */
  _onTypeaheadEnd(typeahead, character) {
    this.focusNext(character);
  }


  /**
   * When the input that corresponds to this instance changes. Allows us to listen
   * and respond to changes made by other components (Calendar Popover, for example).
   * @param {Object} e
   */
  _onInputChange(e) {
    this.isActive = e.target.value ? true : false;
    this._updateClass();

    if (this._pauseInputChange) return;

    this.setValue(e.target.value);
    (this.onChange || noop)(this.inputEl.value, this);
  }
}


/**
* Whitelisted parameters which can be set on construction.
* @type {Array}
*/
TimeInput.prototype._whitelistedParams = [
  'onChange',
  'onFocus',
  'onBlur',
  'validate',
  'onValidate',
  'timeFormat',
  'hourAriaLabel',
  'minuteAriaLabel',
  'periodAriaLabel'
];


/**
* Default values for internal properties we will be setting.
* These are set on each construction so we don't leak properties
* into the prototype chain.
* @type {Object}
*/
TimeInput.prototype.defaults = {
  el: null,
  inputEl: null,
  messageEl: null,
  piecesEl: null,
  timeParts: null,
  hourAriaLabel: null,
  minuteAriaLabel: null,
  periodAriaLabel: null,
  isActive: null,
  inFocus: null,
  timeFormat: null,
  onChange: null,
  onInput: null,
  onFocus: null,
  onBlur: null,
  typeaheads: null,
  typeaheadEls: null,
  runningTypeaheads: false,
  onValidate: null,
  _blurTimer: null,
  _hasFocus: false,
  _pauseInputChange: false,
  _onTypeaheadFocusBound: null,
  _onTypeaheadInputBound: null,
  _onTypeaheadBackspaceBound: null,
  _onTypeaheadEndBound: null,
  _onTypeaheadBlurBound: null,
  _onVisibleChildrenBound: null,
  _onInputChangeBound: null,
};

mixin(TimeInput.prototype, messaging, validation);

export default TimeInput;
